import React, { useEffect, useState } from 'react';
import { type FC, useCallback } from 'react';

import type { TemplateVariant } from '@/api/types';
import { renderText } from '@/etcher';
import type { TextBounds, TextProperties } from '@/etcher/types';

import { useCreativeGeneratorStore } from './store';

export enum ReplacementLayerType {
    Text = 'text',
}

export interface ReplacementLayerRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface TextLayerProps {
    text: string;
    fontSize: number;
    fontFamily: string;
    fontWeight: number;
    fontStyle: string;
    lineHeight: number;
    fill: string;
    autoScaling: boolean;
    charLimit: number;
}

export interface ReplacementLayer {
    layerUuid: string;
    type: ReplacementLayerType;
    rect: ReplacementLayerRect;
    props: TextLayerProps;
}

export interface FrameProps {
    variant: TemplateVariant;
    replacementLayers?: ReplacementLayer[];
}

export const Frame: FC<FrameProps> = ({ variant, replacementLayers }) => {
    const etcherId = useCreativeGeneratorStore((s) => s.etcherId);
    const svgContent = useCreativeGeneratorStore((s) => s.svgFiles[variant.svgFile.file]);
    const [svgNode, setSvgNode] = useState<SVGElement | null>(null);
    const width = variant.svgFile.width;
    const height = variant.svgFile.height;

    const svgRef = useCallback(
        (node: HTMLDivElement) => {
            if (node) {
                const svg = node.querySelector('svg') as SVGElement;
                svg.setAttribute('width', width.toString());
                svg.setAttribute('height', height.toString());
                setSvgNode(svg);
            }
        },
        [width, height],
    );

    useEffect(() => {
        if (replacementLayers && svgNode) {
            // Optimisitcally wait for etcher session to be created
            if (!etcherId) return;

            for (const layer of replacementLayers) {
                const node = svgNode.querySelector(`[data-ntgl-layer-id="${layer.layerUuid}"]`) as SVGElement | null;
                if (!node) return;

                renderText(
                    etcherId,
                    layer.props.text,
                    {
                        font_family: layer.props.fontFamily,
                        font_style: layer.props.fontStyle,
                        font_weight: layer.props.fontWeight.toString(),
                        font_stretch: 'normal',
                        font_size: layer.props.fontSize,
                        line_height: layer.props.lineHeight,
                        color: layer.props.fill,
                        //auto_scaling: layer.props.autoScaling,
                        //char_limit: layer.props.charLimit,
                    } as TextProperties,
                    {
                        width: layer.rect.width,
                        height: layer.rect.height,
                    } as TextBounds,
                    (res) => {
                        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                        g.setAttribute('data-ntgl-layer-id', layer.layerUuid);
                        g.setAttribute('transform', `translate(${layer.rect.x}, ${layer.rect.y})`);
                        g.innerHTML = res.path_data;
                        node.replaceWith(g);
                    },
                );
            }
        }
    }, [etcherId, replacementLayers, svgNode]);

    return (
        svgContent && (
            <div className="flex flex-col justify-center">
                <div className="flex flex-row mb-1 gap-2" style={{ width: `${width}px` }}>
                    <div
                        className="text-white text-xs overflow-hidden whitespace-nowrap text-ellipsis flex-shrink-0 max-w-[60%]"
                        title={variant.name}
                    >
                        {variant.name}
                    </div>
                    <div
                        className="text-gray-110 text-xs overflow-hidden whitespace-nowrap text-ellipsis max-w-[40%]"
                        title={`${width}x${height}`}
                    >
                        {`${width}x${height}`}
                    </div>
                </div>
                <div
                    ref={svgRef}
                    className="border border-gray-150 pointer-events-none"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: will be updated to use a safer method
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                />
            </div>
        )
    );
};
